'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { LOGO_SRC_DARK_BACKGROUND } from '@/components/logo/index';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon, Cancel01Icon } from '@/components/ui/icon/glyphs';
import { Link } from '@/i18n/navigation';
import type { useFundingChrome } from './funding-chrome';
import { MoonPayFooterLink } from './moonpay-footer-link';
type Chrome = ReturnType<typeof useFundingChrome>;
export function FundingDialogHeader(t0) {
  const $ = _c(16);
  const {
    chrome,
    onClose
  } = t0;
  const t = useTranslations("onramp");
  const controls = useTranslations("filters");
  let t1;
  if ($[0] !== chrome.back || $[1] !== chrome.backHref || $[2] !== chrome.hasBack || $[3] !== t) {
    t1 = chrome.hasBack && (chrome.backHref ? <AnimatedButton variant="tertiary" size="sm" className="h-8.25 w-7 p-1" asChild={true}><Link href={chrome.backHref} onClick={chrome.back} aria-label={t("back")}><Icon icon={ArrowLeft01Icon} className="size-4.5" /></Link></AnimatedButton> : <AnimatedButton type="button" variant="tertiary" size="sm" className="h-8.25 w-7 p-1" aria-label={t("back")} prefix={ArrowLeft01Icon} prefixClassName="size-4.5" onClick={chrome.back} />);
    $[0] = chrome.back;
    $[1] = chrome.backHref;
    $[2] = chrome.hasBack;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== chrome.title) {
    t2 = <p aria-hidden="true" className="flex-1 text-heading-md">{chrome.title}</p>;
    $[5] = chrome.title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== controls) {
    t3 = controls("close");
    $[7] = controls;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== onClose || $[10] !== t3) {
    t4 = <AnimatedButton type="button" variant="tertiary" size="sm" className="h-8.25 w-7 p-1" aria-label={t3} prefix={Cancel01Icon} prefixClassName="size-4.5" onClick={onClose} />;
    $[9] = onClose;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t4) {
    t5 = <div className="flex min-h-7 items-center gap-3 pb-4">{t1}{t2}{t4}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
export function FundingDialogFooter(t0) {
  const $ = _c(18);
  const {
    chrome,
    onHistory,
    onProfile,
    direct: t1,
    provider: t2
  } = t0;
  const direct = t1 === undefined ? false : t1;
  const provider = t2 === undefined ? "moonpay" : t2;
  const t = useTranslations("onramp");
  let t3;
  if ($[0] !== chrome.hasProfile || $[1] !== chrome.openProfile || $[2] !== chrome.picker || $[3] !== onHistory || $[4] !== onProfile || $[5] !== t) {
    t3 = chrome.picker && <div className="flex flex-wrap items-center justify-center gap-2">{(onProfile || chrome.hasProfile) && <><button type="button" className="cursor-pointer text-xs text-foreground-secondary underline-offset-4 hover:underline focus-visible:underline" onClick={onProfile ?? chrome.openProfile}>{t("view_kyc_information")}</button><span aria-hidden="true" className="text-xs text-foreground-secondary">·</span></>}<button type="button" className="cursor-pointer text-xs text-foreground-secondary underline-offset-4 hover:underline focus-visible:underline" onClick={onHistory}>{t("view_history")}</button></div>;
    $[0] = chrome.hasProfile;
    $[1] = chrome.openProfile;
    $[2] = chrome.picker;
    $[3] = onHistory;
    $[4] = onProfile;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `flex items-center justify-center ${direct ? "gap-2" : chrome.picker ? "gap-3" : "gap-1.25"}`;
  let t5;
  if ($[7] !== chrome.picker || $[8] !== direct || $[9] !== provider || $[10] !== t) {
    t5 = direct || provider === "morfi" ? <><span className="text-micro">{t("powered_by")}</span><BrandImage src={LOGO_SRC_DARK_BACKGROUND} alt="Morfi" direct={true} /></> : <>{chrome.picker ? null : <span className="text-micro">{t("powered_by")}</span>}<MoonPayFooterLink><BrandImage src="/assets/onramp/moonpay.svg" alt="MoonPay" partnership={chrome.picker} /></MoonPayFooterLink>{chrome.picker && <><span aria-hidden="true" className="text-caption text-foreground-secondary">×</span><BrandImage src={LOGO_SRC_DARK_BACKGROUND} alt="Morfi" /></>}</>;
    $[7] = chrome.picker;
    $[8] = direct;
    $[9] = provider;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t6) {
    t7 = <div className="flex flex-col items-center gap-3 py-2">{t3}{t6}</div>;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function BrandImage(t0) {
  const $ = _c(4);
  const {
    src,
    alt,
    partnership: t1,
    direct: t2
  } = t0;
  const partnership = t1 === undefined ? false : t1;
  const direct = t2 === undefined ? false : t2;
  const t3 = direct ? "h-4.5 w-auto aspect-30/13" : alt === "Morfi" ? "h-6 w-16.75" : partnership ? "h-6 w-21.25" : "h-6 w-20.5";
  let t4;
  if ($[0] !== alt || $[1] !== src || $[2] !== t3) {
    t4 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="rounded-item" imageClassName="object-contain invert dark:invert-0" src={src} alt={alt} size={t3} className="bg-transparent" />;
    $[0] = alt;
    $[1] = src;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}